import {
  GET_CALL_LOGS,
  GET_CALL_METRICS,
  GET_CALL_LOG,
  CREATE_CALL_LOG,
  UPDATE_CALL_LOG,
  DELETE_CALL_LOG,
  GET_CALL_LOGS_BY_CLIENT,
  GET_CALL_LOGS_BY_INQUIRY,
  GET_MY_CALL_LOGS,
} from '../graphql/callLogs';
import {
  CallLog,
  CallLogFilters,
  CallMetrics,
  CreateCallLogInput,
  UpdateCallLogInput,
  ExportData,
  DateRange,
} from '../types/callTracking';

// Type definitions for Apollo queries
interface ListCallLogsData {
  listCallLogs: {
    items: CallLog[];
    nextToken?: string;
  };
}

interface GetCallLogData {
  getCallLog: CallLog;
}

interface CreateCallLogData {
  createCallLog: CallLog;
}

export class CallTrackingService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private client: any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(client: any) {
    this.client = client;
  }

  async getCallLogs(filters?: CallLogFilters, limit = 20, nextToken?: string): Promise<{
    items: CallLog[];
    nextToken?: string;
  }> {
    try {
      console.log('[CallTrackingService] Fetching call logs with:', { filters, limit, nextToken });
      const { data } = await this.client.query({
        query: GET_CALL_LOGS,
        variables: { limit, nextToken, filter: filters },
        fetchPolicy: 'network-only',
      }) as { data: ListCallLogsData };

      console.log('[CallTrackingService] GraphQL response:', data);

      // Handle case where query returns null or undefined
      if (!data || !data.listCallLogs) {
        console.warn('[CallTracking] listCallLogs query returned null');
        return { items: [], nextToken: undefined };
      }

      console.log('[CallTrackingService] Returning items:', data.listCallLogs.items?.length || 0);

      return {
        items: data.listCallLogs.items || [],
        nextToken: data.listCallLogs.nextToken,
      };
    } catch (error) {
      console.error('[CallTracking] Error fetching call logs:', error);
      return { items: [], nextToken: undefined };
    }
  }

  async getCallLog(id: string): Promise<CallLog> {
    try {
      const { data } = await this.client.query({
        query: GET_CALL_LOG,
        variables: { id },
        fetchPolicy: 'cache-first',
      }) as { data: GetCallLogData };

      return data.getCallLog;
    } catch (error) {
      console.error('Error fetching call log:', error);
      throw error;
    }
  }

  async createCallLog(input: CreateCallLogInput): Promise<CallLog> {
    try {
      const { data } = await this.client.mutate({
        mutation: CREATE_CALL_LOG,
        variables: { input },
        refetchQueries: [{ query: GET_CALL_LOGS }, { query: GET_MY_CALL_LOGS }],
      }) as { data: CreateCallLogData };

      return data.createCallLog;
    } catch (error) {
      console.error('Error creating call log:', error);
      throw error;
    }
  }

  async updateCallLog(id: string, input: UpdateCallLogInput): Promise<CallLog> {
    try {
      const { data } = await this.client.mutate({
        mutation: UPDATE_CALL_LOG,
        variables: { id, input },
        refetchQueries: [{ query: GET_CALL_LOGS }, { query: GET_MY_CALL_LOGS }],
      });

      return data.updateCallLog;
    } catch (error) {
      console.error('Error updating call log:', error);
      throw error;
    }
  }

  async deleteCallLog(id: string): Promise<void> {
    try {
      await this.client.mutate({
        mutation: DELETE_CALL_LOG,
        variables: { id },
        refetchQueries: [{ query: GET_CALL_LOGS }, { query: GET_MY_CALL_LOGS }],
      });
    } catch (error) {
      console.error('Error deleting call log:', error);
      throw error;
    }
  }

  async getCallMetrics(dateRange?: DateRange): Promise<CallMetrics> {
    try {
      const { data } = await this.client.query({
        query: GET_CALL_METRICS,
        variables: { 
          startDate: dateRange?.start, 
          endDate: dateRange?.end 
        },
        fetchPolicy: 'network-only',
      });

      // Transform the response to match our interface
      const metrics = data.getCallMetrics;
      return {
        totalCalls: metrics.totalCalls,
        averageDuration: metrics.averageDuration || 0,
        totalDuration: metrics.totalCalls * (metrics.averageDuration || 0),
        successfulCalls: metrics.outcomeDistribution?.find((item: any) => item.outcome === 'ANSWERED')?.count || 0,
        conversionRate: metrics.totalCalls > 0 ? 
          (metrics.outcomeDistribution?.find((item: any) => item.outcome === 'ANSWERED')?.count || 0) / metrics.totalCalls : 0,
        outcomeBreakdown: metrics.outcomeDistribution?.reduce((acc: any, item: any) => {
          acc[item.outcome] = item.count;
          return acc;
        }, {}) || {},
        staffPerformance: metrics.callsByStaff?.map((staff: any) => ({
          staffId: staff.staffId,
          staffName: staff.staffName,
          totalCalls: staff.count,
          averageDuration: metrics.averageDuration || 0,
          conversionRate: 0.7, // Mock conversion rate
          outcomeBreakdown: {
            'ANSWERED': Math.floor(staff.count * 0.7),
            'VOICEMAIL': Math.floor(staff.count * 0.2),
            'NO_ANSWER': Math.floor(staff.count * 0.1),
          },
        })) || [],
        trendsOverTime: [], // Not available in current schema
      };
    } catch (error) {
      console.error('Error fetching call metrics:', error);
      throw error;
    }
  }

  async getCallLogsByClient(clientId: string, limit = 20, nextToken?: string): Promise<{
    items: CallLog[];
    nextToken?: string;
  }> {
    try {
      const { data } = await this.client.query({
        query: GET_CALL_LOGS_BY_CLIENT,
        variables: { clientId, limit, nextToken },
        fetchPolicy: 'network-only',
      });

      return {
        items: data.listCallLogsByClient.items || [],
        nextToken: data.listCallLogsByClient.nextToken,
      };
    } catch (error) {
      console.error('Error fetching call logs by client:', error);
      throw error;
    }
  }

  async getCallLogsByInquiry(inquiryId: string, limit = 20, nextToken?: string): Promise<{
    items: CallLog[];
    nextToken?: string;
  }> {
    try {
      const { data } = await this.client.query({
        query: GET_CALL_LOGS_BY_INQUIRY,
        variables: { inquiryId, limit, nextToken },
        fetchPolicy: 'network-only',
      });

      return {
        items: data.listCallLogsByInquiry.items || [],
        nextToken: data.listCallLogsByInquiry.nextToken,
      };
    } catch (error) {
      console.error('Error fetching call logs by inquiry:', error);
      throw error;
    }
  }

  async getMyCallLogs(limit = 20, nextToken?: string): Promise<{
    items: CallLog[];
    nextToken?: string;
  }> {
    try {
      const { data } = await this.client.query({
        query: GET_MY_CALL_LOGS,
        variables: { limit, nextToken },
        fetchPolicy: 'network-only',
      });

      return {
        items: data.listMyCallLogs.items || [],
        nextToken: data.listMyCallLogs.nextToken,
      };
    } catch (error) {
      console.error('Error fetching my call logs:', error);
      throw error;
    }
  }

  async exportCallData(filters?: CallLogFilters, format: 'csv' | 'xlsx' | 'json' = 'csv'): Promise<ExportData> {
    try {
      // Fetch all call logs matching the filters
      const { items } = await this.getCallLogs(filters, 1000); // Large limit for export

      const filename = `call-logs-${new Date().toISOString().split('T')[0]}.${format}`;

      return {
        data: items,
        format,
        filename,
      };
    } catch (error) {
      console.error('Error exporting call data:', error);
      throw error;
    }
  }

  // Utility methods for formatting and calculations
  formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  formatPhoneNumber(phoneNumber: string): string {
    // Basic US phone number formatting
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phoneNumber;
  }

  getOutcomeColor(outcome: string): string {
    const colors: Record<string, string> = {
      ANSWERED: 'text-green-600 bg-green-50',
      VOICEMAIL: 'text-yellow-600 bg-yellow-50',
      NO_ANSWER: 'text-gray-600 bg-gray-50',
      BUSY: 'text-orange-600 bg-orange-50',
      UNKNOWN: 'text-red-600 bg-red-50',
    };
    return colors[outcome] || 'text-gray-600 bg-gray-50';
  }

  getOutcomeLabel(outcome: string): string {
    const labels: Record<string, string> = {
      ANSWERED: 'Answered',
      VOICEMAIL: 'Voicemail',
      NO_ANSWER: 'No Answer',
      BUSY: 'Busy',
      UNKNOWN: 'Unknown',
    };
    return labels[outcome] || outcome;
  }
}